import { useParams, Link } from 'react-router-dom';
import { getComicBySlug, getRelatedComics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return (
      <main className="pt-24 pb-16 bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-400">
            <i className="ri-error-warning-line text-4xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-800 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">抱歉，您要找的漫画不存在或已下架。</p>
          <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
            浏览全部漫画
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = getRelatedComics(comic);
  const categoryInfo = categories.find((c) => c.slug === comic.categorySlug);

  return (
    <main className="pt-20 pb-16 bg-background-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
            {categoryInfo?.name || comic.category}漫画
          </Link>
          <span>/</span>
          <span className="text-foreground-800 font-medium">{comic.title}</span>
        </nav>

        {/* Article Header */}
        <article className="mb-12">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover */}
            <div className="flex-shrink-0">
              <div className="w-[180px] h-[240px] md:w-[220px] md:h-[293px] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 免费韩漫下拉式阅读`}
                  title={`${comic.title} - 免费韩漫下拉式阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-600 mb-4">
                作者：<strong>{comic.author}</strong>
              </p>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'}`}>
                  {comic.status}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <div className="w-3 h-3 inline-flex items-center justify-center mr-1">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                  </div>
                  {comic.rating} 分
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <div className="w-3 h-3 inline-flex items-center justify-center mr-1">
                    <i className="ri-eye-line text-xs"></i>
                  </div>
                  {(comic.views / 10000).toFixed(0)} 万次阅读
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <div className="w-3 h-3 inline-flex items-center justify-center mr-1">
                    <i className="ri-heart-line text-xs"></i>
                  </div>
                  {(comic.favorites / 10000).toFixed(0)} 万收藏
                </span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-[11px] bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200 transition-colors border border-background-300 whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className="ri-heart-line text-sm"></i>
                  </div>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Full Description */}
        <section className="mb-12 p-6 bg-background-100 rounded-xl border border-background-200/70">
          <h2 className="text-lg font-semibold font-heading text-foreground-800 mb-4">作品简介</h2>
          <p className="text-sm text-foreground-600 leading-relaxed">{comic.longDescription}</p>
        </section>

        {/* Chapter List */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold font-heading text-foreground-800 mb-4">章节列表</h2>
          <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="divide-y divide-background-200/60">
              {comic.chapters.slice().reverse().map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 font-medium">{chapter.title}</span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{chapter.updatedAt}</span>
                </div>
              ))}
            </div>
          </div>
          {comic.chapters.length > 10 && (
            <div className="text-center mt-4">
              <button className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                展开全部 ({comic.chapters.length}) 话 <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
            </div>
          )}
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="mb-12">
            <h2 className="text-lg font-semibold font-heading text-foreground-800 mb-5">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedComics.map((rc) => (
                <ComicCard key={rc.id} comic={rc} size="small" />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": `${comic.title} - 免费漫画网`,
        "author": { "@type": "Person", "name": comic.author },
        "datePublished": comic.chapters[0]?.updatedAt || comic.updatedAt,
        "dateModified": comic.updatedAt,
        "description": comic.description,
        "image": comic.coverImage,
        "publisher": {
          "@type": "Organization",
          "name": "免费漫画网",
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.freemanhua.com/article/${comic.slug}`,
        },
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.freemanhua.com/" },
          { "@type": "ListItem", "position": 2, "name": `${categoryInfo?.name || comic.category}漫画`, "item": `https://www.freemanhua.com/category/${comic.categorySlug}` },
          { "@type": "ListItem", "position": 3, "name": comic.title, "item": `https://www.freemanhua.com/article/${comic.slug}` },
        ],
      }) }} />
    </main>
  );
}